import { useMemo, type ReactNode } from 'react';
import { AuthProvider as OidcProvider, useAuth } from 'react-oidc-context';
import { AUTH_MODE } from './mode';
import { getUserManager } from './userManager';
import { AuthStateContext, type AuthStateValue } from './authState';

/** Bridges react-oidc-context into our AuthStateContext. */
function OidcBridge({ children }: Readonly<{ children: ReactNode }>) {
  const auth = useAuth();

  // Token syncing is handled OUTSIDE render so this stays a pure component:
  //  • first paint — main.tsx seeds the token from any still-valid stored session
  //    before React mounts (see primeAccessToken), so `useIdentity` (claims decoded
  //    from the token) and the initial API requests have a bearer;
  //  • signin / silent-renew / logout — the userManager addUserLoaded /
  //    addUserUnloaded events keep it in lockstep;
  //  • a mid-session 401 — the baseQuery silent-renew path repairs it.
  // Writing the token here during render would be an impure side effect (unsafe
  // under concurrent rendering, where a render can be discarded before commit); an
  // effect would run too late — child effects fire bearer-less requests first.
  const { isAuthenticated, isLoading, signinRedirect, signoutRedirect } = auth;
  // Memoised so consumers re-render only when the auth state actually changes,
  // not on every render of this bridge.
  const value = useMemo<AuthStateValue>(
    () => ({
      isAuthenticated,
      isLoading,
      signinRedirect: () => void signinRedirect(),
      signoutRedirect: () => void signoutRedirect(),
    }),
    [isAuthenticated, isLoading, signinRedirect, signoutRedirect],
  );
  return <AuthStateContext.Provider value={value}>{children}</AuthStateContext.Provider>;
}

/** dev/msw modes: treat the user as authenticated with no OIDC. */
const STATIC_AUTHED: AuthStateValue = {
  isAuthenticated: true,
  isLoading: false,
  signinRedirect: () => {},
  signoutRedirect: () => {},
};

export function AuthProvider({ children }: Readonly<{ children: ReactNode }>) {
  if (AUTH_MODE === 'oidc') {
    const userManager = getUserManager();
    return (
      <OidcProvider
        userManager={userManager}
        onSigninCallback={() => {
          globalThis.history.replaceState({}, document.title, globalThis.location.pathname);
        }}
      >
        <OidcBridge>{children}</OidcBridge>
      </OidcProvider>
    );
  }
  return <AuthStateContext.Provider value={STATIC_AUTHED}>{children}</AuthStateContext.Provider>;
}
